import React from 'react';
import { ChevronLeft, ChevronRight, Phone, MapPin, Calendar, ExternalLink, Share2, MessageSquare } from 'lucide-react';

/**
 * RCS Mobile Preview Component
 * Renders a realistic mobile phone preview of RCS messages
 */
export default function RCSMobilePreview({ 
  content, 
  senderName = "B2RCS", 
  senderLogo = null,
  className = "" 
}) {
  const [carouselIndex, setCarouselIndex] = React.useState(0);

  if (!content) {
    return (
      <div className={`relative ${className}`}>
        <PhoneFrame>
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Aperçu du message
          </div>
        </PhoneFrame>
      </div>
    );
  }

  const { message_type, content: messageContent } = content;

  const renderSuggestion = (suggestion, index) => {
    const getIcon = () => {
      switch (suggestion.type) {
        case 'url': return <ExternalLink className="w-3 h-3" />;
        case 'dial': return <Phone className="w-3 h-3" />;
        case 'location': return <MapPin className="w-3 h-3" />;
        case 'share_location': return <Share2 className="w-3 h-3" />;
        case 'calendar': return <Calendar className="w-3 h-3" />;
        default: return <MessageSquare className="w-3 h-3" />;
      }
    };

    return (
      <button
        key={index}
        className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-blue-500 text-blue-600 rounded-full text-xs font-medium hover:bg-blue-50 transition-colors"
      >
        {getIcon()}
        <span>{suggestion.text || 'Action'}</span>
      </button>
    );
  };

  const renderTextMessage = () => (
    <div className="space-y-3">
      <div className="bg-gray-100 rounded-2xl rounded-tl-sm p-3 max-w-[85%]">
        <p className="text-sm text-gray-800 whitespace-pre-wrap">
          {messageContent?.text || 'Votre message...'}
        </p>
      </div>
      {messageContent?.suggestions?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {messageContent.suggestions.map((s, i) => renderSuggestion(s, i))}
        </div>
      )}
    </div>
  );

  const renderMediaMessage = () => (
    <div className="space-y-3">
      <div className="rounded-2xl rounded-tl-sm overflow-hidden max-w-[85%]">
        {messageContent?.media_url ? (
          <img 
            src={messageContent.media_url} 
            alt="Media" 
            className="w-full h-40 object-cover"
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="150" viewBox="0 0 200 150"><rect fill="%23e5e7eb" width="200" height="150"/><text x="100" y="75" font-family="Arial" font-size="14" fill="%239ca3af" text-anchor="middle">Image</text></svg>';
            }}
          />
        ) : (
          <div className="w-full h-40 bg-gray-200 flex items-center justify-center text-gray-400">
            Média
          </div>
        )}
      </div>
      {messageContent?.suggestions?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {messageContent.suggestions.map((s, i) => renderSuggestion(s, i))}
        </div>
      )}
    </div>
  );

  const renderRichCard = () => {
    const heightClasses = {
      SHORT: 'h-24',
      MEDIUM: 'h-36',
      TALL: 'h-48'
    };
    const mediaHeight = heightClasses[messageContent?.media_height] || heightClasses.MEDIUM;

    return (
      <div className="space-y-3">
        <div className="bg-white rounded-2xl rounded-tl-sm overflow-hidden shadow-sm border max-w-[85%]">
          {/* Media */}
          {messageContent?.media_url && (
            <div className={`${mediaHeight} bg-gray-200`}>
              <img 
                src={messageContent.media_url} 
                alt="Card media" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="150" viewBox="0 0 200 150"><rect fill="%23e5e7eb" width="200" height="150"/><text x="100" y="75" font-family="Arial" font-size="14" fill="%239ca3af" text-anchor="middle">Image</text></svg>';
                }}
              />
            </div>
          )}
          
          {/* Content */}
          <div className="p-3">
            {messageContent?.title && (
              <h3 className="font-semibold text-gray-900 text-sm mb-1">
                {messageContent.title}
              </h3>
            )}
            {messageContent?.description && (
              <p className="text-xs text-gray-600 line-clamp-3">
                {messageContent.description}
              </p>
            )}
          </div>
          
          {/* Card suggestions */}
          {messageContent?.suggestions?.length > 0 && (
            <div className="border-t px-3 py-2 flex flex-wrap gap-2">
              {messageContent.suggestions.slice(0, 4).map((s, i) => (
                <button
                  key={i}
                  className="text-xs text-blue-600 font-medium hover:underline flex items-center gap-1"
                >
                  {s.text || 'Action'}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCarousel = () => {
    const cards = messageContent?.cards || [];
    const activeCard = cards[carouselIndex] || {};

    return (
      <div className="space-y-3">
        <div className="relative">
          {/* Navigation buttons */}
          {cards.length > 1 && (
            <>
              <button
                onClick={() => setCarouselIndex(Math.max(0, carouselIndex - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-6 h-6 bg-white rounded-full shadow flex items-center justify-center disabled:opacity-50"
                disabled={carouselIndex === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCarouselIndex(Math.min(cards.length - 1, carouselIndex + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-6 h-6 bg-white rounded-full shadow flex items-center justify-center disabled:opacity-50"
                disabled={carouselIndex === cards.length - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
          
          {/* Card */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border mx-4">
            {/* Media */}
            <div className="h-32 bg-gray-200">
              {activeCard.media_url ? (
                <img 
                  src={activeCard.media_url} 
                  alt="Card" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="150" viewBox="0 0 200 150"><rect fill="%23e5e7eb" width="200" height="150"/><text x="100" y="75" font-family="Arial" font-size="14" fill="%239ca3af" text-anchor="middle">Image</text></svg>';
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                  Image {carouselIndex + 1}
                </div>
              )}
            </div>
            
            {/* Content */}
            <div className="p-3">
              <h3 className="font-semibold text-gray-900 text-sm mb-1">
                {activeCard.title || `Carte ${carouselIndex + 1}`}
              </h3>
              {activeCard.description && (
                <p className="text-xs text-gray-600 line-clamp-2">
                  {activeCard.description}
                </p>
              )}
            </div>
            
            {/* Card actions */}
            {activeCard.suggestions?.length > 0 && (
              <div className="border-t px-3 py-2">
                {activeCard.suggestions.slice(0, 2).map((s, i) => (
                  <button
                    key={i}
                    className="text-xs text-blue-600 font-medium block py-1"
                  >
                    {s.text || 'Action'}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Carousel indicators */}
        <div className="flex justify-center gap-1">
          {cards.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCarouselIndex(idx)}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${
                idx === carouselIndex ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderMessage = () => {
    switch (message_type) {
      case 'text':
        return renderTextMessage();
      case 'media':
        return renderMediaMessage();
      case 'rich_card':
        return renderRichCard();
      case 'carousel':
        return renderCarousel();
      default:
        return renderTextMessage();
    }
  };

  return (
    <div className={`relative ${className}`} data-testid="rcs-mobile-preview">
      <PhoneFrame>
        {/* Header */}
        <div className="bg-white border-b px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            {senderLogo ? (
              <img src={senderLogo} alt={senderName} className="w-full h-full rounded-full object-cover" />
            ) : (
              <span className="text-indigo-600 font-bold text-sm">
                {senderName.charAt(0)}
              </span>
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">{senderName}</p>
            <p className="text-xs text-green-600 flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              Message RCS vérifié
            </p>
          </div>
        </div>
        
        {/* Messages area */}
        <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
          {renderMessage()}
        </div>
        
        {/* Input area */}
        <div className="bg-white border-t px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-100 rounded-full px-4 py-2 text-sm text-gray-400">
              Message...
            </div>
            <button className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </PhoneFrame>
    </div>
  );
}

// Phone frame component
function PhoneFrame({ children }) {
  return (
    <div className="relative mx-auto" style={{ width: '320px' }}>
      {/* Phone outer frame */}
      <div className="relative bg-gray-900 rounded-[3rem] p-3 shadow-xl">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-2xl z-10"></div>
        
        {/* Screen */}
        <div className="bg-white rounded-[2.5rem] overflow-hidden" style={{ height: '580px' }}>
          {/* Status bar */}
          <div className="bg-white h-8 flex items-center justify-between px-6 text-xs">
            <span className="font-semibold">9:41</span>
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
              </svg>
              <div className="flex items-center">
                <div className="w-6 h-3 border border-current rounded-sm relative">
                  <div className="absolute inset-0.5 bg-current rounded-sm" style={{ width: '80%' }}></div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex flex-col" style={{ height: 'calc(100% - 32px)' }}>
            {children}
          </div>
        </div>
      </div>
      
      {/* Side buttons */}
      <div className="absolute left-0 top-24 w-1 h-8 bg-gray-800 rounded-l"></div>
      <div className="absolute left-0 top-36 w-1 h-12 bg-gray-800 rounded-l"></div>
      <div className="absolute left-0 top-52 w-1 h-12 bg-gray-800 rounded-l"></div>
      <div className="absolute right-0 top-32 w-1 h-16 bg-gray-800 rounded-r"></div>
    </div>
  );
}
